/**
 * Armed-skill directive for agent chat (mirrors the custom-instructions
 * wrapper in custom-instructions-popover.tsx).
 *
 * "Arming" a skill from the composer prefixes every outgoing message with
 * a [USE SKILL] block that tells the orchestrator to mount the org skill
 * through its real seam -- the `load_skill` tool (see agentic-backend
 * services/orchestrator/tools/skill_tools.py). Mounts persist across turns
 * server-side; repeating the directive while the skill is armed keeps the
 * instruction deterministic per turn. The wrapped form is what the server
 * persists, and MessageBubble strips it for display.
 */

import { CUSTOM_INSTRUCTIONS_RE } from '../custom-instructions-popover';

export interface ArmedSkill {
  skillId: string;
  name: string;
}

export const USE_SKILL_RE =
  /^\[USE SKILL\]\r?\nUse the org skill "([^"\r\n]*)" \(skill_id: ([^)\r\n]*)\)[\s\S]*?\r?\n\[\/USE SKILL\]\r?\n\r?\n/;

export function wrapWithSkillDirective(
  message: string,
  skill: ArmedSkill | null | undefined,
): string {
  if (!skill) return message;
  // Quotes are stripped from the name so the block always round-trips
  // through USE_SKILL_RE when reloaded from the server.
  const safeName = skill.name.replace(/"/g, '');
  return (
    `[USE SKILL]\n` +
    `Use the org skill "${safeName}" (skill_id: ${skill.skillId}) for this ` +
    `request: mount it with load_skill("${skill.skillId}") unless ` +
    `list_mounted_skills already shows it, then follow its instructions.\n` +
    `[/USE SKILL]\n\n` +
    message
  );
}

export function stripSkillDirective(content: string): {
  text: string;
  skill?: ArmedSkill;
} {
  const match = content.match(USE_SKILL_RE);
  if (!match) return { text: content };
  return {
    text: content.replace(USE_SKILL_RE, ''),
    skill: { name: match[1], skillId: match[2] },
  };
}

/**
 * Sanitizes a conversation title/preview for display. Server-side summaries
 * truncate the raw first message, so a leading directive block may arrive
 * without its closing tag; strip complete blocks first, then drop any
 * unterminated leading block wholesale.
 */
export function stripDirectivesForLabel(raw: string): string {
  let text = raw;
  // Two passes: the blocks can appear in either order.
  for (let i = 0; i < 2; i++) {
    text = text.replace(CUSTOM_INSTRUCTIONS_RE, '').replace(USE_SKILL_RE, '');
  }
  if (/^\[(CUSTOM INSTRUCTIONS|USE SKILL)\]/.test(text)) {
    // Truncated block: everything visible belongs to the directive.
    return '';
  }
  return text.trim();
}
